import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { FirebaseService } from '../services/firebaseService';

interface EclairagePublicCategoryProps {
  t: any;
  isRTL: boolean;
}

const CategoryPage = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding-top: 120px;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  padding-bottom: 4rem;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Breadcrumbs = styled(motion.div)<{ $isRTL: boolean }>`
  padding: 1rem 0;
  font-size: 0.9rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const BreadcrumbLink = styled.span<{ $isLast: boolean }>`
  color: ${props => props.$isLast ? '#dc2626' : '#64748b'};
  text-decoration: none;
  font-weight: ${props => props.$isLast ? '600' : '400'};
  cursor: ${props => props.$isLast ? 'default' : 'pointer'};
  transition: color 0.3s ease;
  
  &:hover {
    color: ${props => props.$isLast ? '#dc2626' : '#dc2626'};
  }
`;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: 5rem;
  position: relative;
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%);
  border-radius: 20px;
  z-index: 0;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  padding: 3rem 2rem;
`;

const PageTitle = styled.h1<{ $isRTL: boolean }>`
  font-size: 3.5rem;
  font-weight: 800;
  color: #1f2937;
  margin: 0 0 1.5rem 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageDescription = styled.p<{ $isRTL: boolean }>`
  font-size: 1.3rem;
  color: #64748b;
  max-width: 900px;
  margin: 0 auto 2rem auto;
  line-height: 1.7;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  font-weight: 400;
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const StatCard = styled.div<{ $isRTL: boolean }>`
  background: white;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  text-align: center;
  border: 1px solid rgba(220, 38, 38, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    border-color: rgba(220, 38, 38, 0.2);
  }
`;

const StatNumber = styled.div<{ $isRTL: boolean }>`
  font-size: 2.5rem;
  font-weight: 700;
  color: #dc2626;
  margin-bottom: 0.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const StatLabel = styled.div<{ $isRTL: boolean }>`
  font-size: 1rem;
  color: #64748b;
  font-weight: 500;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const SubcategoriesSection = styled.div`
  margin-top: 4rem;
`;

const SectionTitle = styled.h2<{ $isRTL: boolean }>`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
  text-align: center;
  margin-bottom: 3rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const SubcategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2.5rem;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const SubcategoryCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  transition: all 0.4s ease;
  cursor: pointer;
  border: 1px solid rgba(220, 38, 38, 0.1);
  position: relative;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    border-color: rgba(220, 38, 38, 0.3);
    
    &::before {
      opacity: 1;
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #dc2626, #ef4444);
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1;
  }
`;

const SubcategoryImage = styled.div`
  height: 250px;
  width: 100%;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(220, 38, 38, 0.05) 0%, rgba(239, 68, 68, 0.05) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  ${SubcategoryCard}:hover &::after {
    opacity: 1;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
  }
  
  ${SubcategoryCard}:hover & img {
    transform: scale(1.05);
  }
`;

const SubcategoryContent = styled.div<{ $isRTL: boolean }>`
  padding: 2.5rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const SubcategoryTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 1rem 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const SubcategoryDescription = styled.p<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  color: #64748b;
  line-height: 1.6;
  margin: 0 0 2rem 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const ViewDetailsButton = styled.button<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  box-shadow: 0 4px 15px rgba(220, 38, 38, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(220, 38, 38, 0.4);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  font-size: 1.2rem;
  color: #64748b;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EmptyStateTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const EmptyStateText = styled.p<{ $isRTL: boolean }>`
  color: #64748b;
  font-size: 1.1rem;
  line-height: 1.6;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

interface Subcategory {
  id: string;
  name: string;
  description: string;
  image: string;
  createdAt: Date;
  updatedAt: Date;
}

const EclairagePublicCategory: React.FC<EclairagePublicCategoryProps> = ({ t, isRTL }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const navigate = useNavigate();
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadSubcategories();
  }, []);

  const loadSubcategories = async () => {
    try {
      setLoading(true);
      const data = await FirebaseService.getAll('eclairage-public-subcategories');
      setSubcategories(data);
    } catch (error) {
      console.error('Error loading subcategories:', error);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbs = [
    { label: t.home, path: "/" },
    { label: t.catalogTitle, path: "/catalogue" },
    { label: t.publicLightingTitle, path: "/category/eclairage-public" },
  ];

  const handleSubcategoryClick = (subcategoryId: string) => {
    navigate(`/category/eclairage-public/subcategory/${subcategoryId}`);
  };

  // Function to get translated name for subcategory
  const getTranslatedSubcategoryName = (subcategoryName: string) => {
    const nameMap: { [key: string]: string } = {
      'Gamme d\'éclairage Fonctionnel': t.functionalLightingTitle,
      'Gamme d\'éclairage décoratif': t.decorativeLightingTitle,
      'Supports de signalisation': t.signageSupportsTitle,
      'Gamme d\'éclairage sportif et grands espaces': t.sportsLightingTitle,
    };
    return nameMap[subcategoryName] || subcategoryName;
  };

  if (loading) {
    return (
      <CategoryPage $isRTL={isRTL}>
        <Container>
          <LoadingSpinner>{t.loadingSubcategoriesLighting}</LoadingSpinner>
        </Container>
      </CategoryPage>
    );
  }

  return (
    <CategoryPage $isRTL={isRTL}>
      <Container>
        <Breadcrumbs $isRTL={isRTL}>
          {breadcrumbs.map((breadcrumb, index) => (
            <React.Fragment key={index}>
              <BreadcrumbLink
                $isLast={index === breadcrumbs.length - 1}
                onClick={() => index < breadcrumbs.length - 1 && navigate(breadcrumb.path)}
              >
                {breadcrumb.label}
              </BreadcrumbLink>
              {index < breadcrumbs.length - 1 && <span>›</span>}
            </React.Fragment>
          ))}
        </Breadcrumbs>


        <SubcategoriesSection>
          <SectionTitle $isRTL={isRTL}>{t.ourLightingRanges}</SectionTitle>
          
          {subcategories.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>���</EmptyStateIcon>
              <EmptyStateTitle $isRTL={isRTL}>{t.noRangesAvailable}</EmptyStateTitle>
              <EmptyStateText $isRTL={isRTL}>
                {t.rangesComingSoon}
              </EmptyStateText>
            </EmptyState>
          ) : (
            <SubcategoriesGrid ref={ref}>
              {subcategories.map((subcategory, index) => (
                <SubcategoryCard
                  key={subcategory.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  onClick={() => handleSubcategoryClick(subcategory.id)}
                >
                  <SubcategoryImage>
                      <img
                        src={subcategory.image}
                        alt={subcategory.name}
                        loading="lazy"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/images/products/default-product.jpg';
                        }}
                      />
                  </SubcategoryImage>
                  <SubcategoryContent $isRTL={isRTL}>
                    <SubcategoryTitle $isRTL={isRTL}>{getTranslatedSubcategoryName(subcategory.name)}</SubcategoryTitle>
                    <SubcategoryDescription $isRTL={isRTL}>
                      {subcategory.description}
                    </SubcategoryDescription>
                    <ViewDetailsButton $isRTL={isRTL}>
                      {t.discoverEnsembles} {isRTL ? '←' : '→'}
                    </ViewDetailsButton>
                  </SubcategoryContent>
                </SubcategoryCard>
              ))}
            </SubcategoriesGrid>
          )}
        </SubcategoriesSection>
      </Container>
    </CategoryPage>
  );
};

export default EclairagePublicCategory;
